import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  GenerateLeadBody,
  PurchaseOrderDetailsResponse,
  PurchaseOrdersResponse,
  SummaryResponse,
  SuppliersResponse,
  SupplierStatsResponse,
} from './types';

export const getSummary = (dispatch: Dispatch, query?: string) => {
  return useQuery({
    queryKey: ['publicMarket', 'summary', ...(query ? [query] : [])],
    queryFn: async (): Promise<SummaryResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/publicMarket/summary${query ? `?${query}` : ''}`,
        dispatch,
      });
      if (response) return response.data;
    },
  });
};

export const getSupplierStats = (
  dispatch: Dispatch,
  identifier: string,
  query?: string,
) => {
  return useQuery({
    queryKey: [
      'publicMarket',
      'supplierStats',
      identifier,
      ...(query ? [query] : []),
    ],
    queryFn: async (): Promise<SupplierStatsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/publicMarket/supplier/${identifier}?${query}`,
        dispatch,
      });
      if (response) return response.data;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
  });
};

export const getTenderDetails = async (
  dispatch: Dispatch,
  tenderId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/publicMarket/tender/${tenderId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPurchaseOrderDetails = async (
  dispatch: Dispatch,
  purchaseOrderId: string,
): Promise<PurchaseOrderDetailsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/publicMarket/purchaseOrder/${purchaseOrderId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getTenders = async (dispatch: Dispatch, query?: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/publicMarket/tenders?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPurchaseOrders = async (
  dispatch: Dispatch,
  query?: string,
): Promise<PurchaseOrdersResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/publicMarket/purchaseOrders?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const generateLead = async (
  dispatch: Dispatch,
  data: GenerateLeadBody,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/publicMarket/lead`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const generateLeads = async (
  dispatch: Dispatch,
  data: GenerateLeadBody[],
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/publicMarket/lead/batch`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const getSuppliers = async (
  dispatch: Dispatch,
  query?: string,
): Promise<SuppliersResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/publicMarket/suppliers?${query}`,
    dispatch,
  });
  if (response) return response.data;
};
